import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAccount } from "wagmi";
import { isAddress, type Address } from "viem";
import { byShortName } from "@/lib/chains";
import { api } from "@/lib/api";
import { useDropReads } from "@/hooks/useDropReads";
import { useAllowlistSign } from "@/hooks/useAllowlistSign";
import { MintCard } from "@/components/publicmint/MintCard";
import { MintedGallery } from "@/components/publicmint/MintedGallery";

type Collection = {
  id: string;
  chainId: number;
  contractAddress: Address;
  name: string;
  symbol: string;
  description?: string;
  imageCid?: string;
  bannerCid?: string;
  kind: string;
  creatorAddress?: Address;
};

/**
 * Public mint page (`/<chain>/<address>`). The page itself stays thin: it
 * resolves the chain + contract from URL params, fetches collection
 * metadata + on-chain reads, derives presale state, and stitches a
 * MintCard + MintedGallery together.
 */
export function PublicMint() {
  const { shortName, address: addrParam } = useParams<{ shortName: string; address: string }>();
  const chain = shortName ? byShortName(shortName) : undefined;
  const address = (addrParam ?? "").toLowerCase() as Address;
  const valid = !!chain && isAddress(address);

  const { address: wallet } = useAccount();
  const [justMinted, setJustMinted] = useState<Set<string>>(() => new Set());

  const { data: collection } = useQuery({
    queryKey: ["collection", chain?.chainId, address],
    queryFn: () => api.get<Collection>(`/api/collections/${chain!.chainId}/${address}`),
    enabled: valid,
  });

  const reads = useDropReads({
    address,
    chainId: chain?.chainId ?? 0,
    enabled: valid,
  });

  // Pre-derive presale flags so we can declare the allowlist signature query
  // before the !valid early return — rules of hooks require every render to
  // call the same hooks in the same order.
  const presaleConfigured =
    reads.publicMintAt > 0 &&
    !!reads.onChainSigner &&
    reads.onChainSigner !== "0x0000000000000000000000000000000000000000";
  const inPresale =
    presaleConfigured && Math.floor(Date.now() / 1000) < reads.publicMintAt;

  const allowlist = useAllowlistSign({
    chainId: chain?.chainId,
    address,
    wallet,
    enabled: valid && !!wallet && inPresale,
  });

  if (!valid) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center">
        <h1 className="mb-2 text-xl font-semibold">Collection not found</h1>
        <p className="text-sm text-muted-foreground">
          The URL <code>/{shortName}/{addrParam}</code> doesn't match a configured chain and contract.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <MintCard
        chain={chain}
        address={address}
        collection={collection}
        reads={reads}
        allowlist={allowlist}
        onMinted={(ids) => setJustMinted((prev) => new Set([...prev, ...ids]))}
      />
      <MintedGallery
        shortName={shortName ?? `c${chain.chainId}`}
        address={address}
        chainId={chain.chainId}
        totalMinted={Number(reads.totalMinted)}
        maxSupply={reads.maxSupply}
        ipfsGateway={chain.ipfsGateway ?? "https://dweb.link/ipfs/"}
        wallet={wallet}
        justMinted={justMinted}
      />
    </div>
  );
}
